import React, { useEffect, useState } from 'react';
import { NativeSyntheticEvent, TextInputKeyPressEventData } from 'react-native';
import { translate } from '../../../services/translate';
import { Pagination } from '../../../components/Pagination';
import { RouteProp, useIsFocused, useRoute } from '@react-navigation/native';
import { ThirdPartyApprovedCompaniesParamList } from '../../../typings/Params';
import MyDataTable from '../../../components/MyDataTable';
import FilterInput from '../../../components/Filter/components/FilterInput';
import Filter from './Filters';
import useFilters from '../../../components/Filter/hooks/useFilters';
import Toast from 'react-native-toast-message';
import DeleteModal from '../../../components/DeleteModal';
import { ClientError } from '../../../services/central-api/base-service';
import moment from 'moment';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import DataTableHeader from '../../../components/DataTableHeader';
import DataTableContainer from '../../../components/DataTableContainer';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../../context/Auth';
import { thirdPartyApprovedCompaniesService } from '../../../services/central-api/third-party-approved-companies';
import DropdownButton from '../../../components/DropdownButton';

export default function DonatedCamerasList({ navigation }: { navigation: Navigation; }) {
    const { user: authenticatedUser } = useAuth();

    const [isRejectLoading, setIsRejectLoading] = useState<boolean>(false);
    const [cameras, setCameras] = useState<PaginatedDonatedCamera[]>([]);
    const [selectedCamera, setSelectedCamera] = useState<PaginatedDonatedCamera>();
    const [rejectModalVisible, setRejectModalVisible] = useState<boolean>(false);

    const route = useRoute<RouteProp<ThirdPartyApprovedCompaniesParamList, 'DonatedCamerasList'>>();
    const [filters, setFilters] = useFilters<ThirdPartyApprovedCompaniesParamList['DonatedCamerasList']>({
        ...route.params,
        page: Math.max(route.params.page - 1, 0),
        limit: route.params.limit ?? 25,
    });
    const [textFilter, setTextFilter] = useState(filters.textFilter ?? '');
    const [totalItems, setTotalItems] = useState(0);

    const isFocused = useIsFocused();
    const queryResult = useQuery({
        queryKey: ['camerasDonatedCameras', filters],
        queryFn: () => thirdPartyApprovedCompaniesService.getDonatedCamerasPaginated(filters),
        enabled: !!authenticatedUser && isFocused,
    });

    const isEnabled = !!authenticatedUser;
    const isLoading = isEnabled && queryResult.isFetching;

    const { data, refetch } = queryResult;

    useEffect(() => {
        if (!data) {
            return;
        }
        setCameras(data.rows);
        setTotalItems(data.count);
    }, [data]);

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            navigation.setParams({
                ...filters,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 1,
            });
            setFilters(old => ({
                ...old,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 0,
            }));
        }
    }

    async function confirmDelete(donatedCamera: PaginatedDonatedCamera) {
        try {
            setIsRejectLoading(true);
            await thirdPartyApprovedCompaniesService.deleteDonatedCamera(donatedCamera.id);
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });

            setRejectModalVisible(false);
            refetch();
        } catch (err) {
            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }

            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        } finally {
            setIsRejectLoading(false);
            setRejectModalVisible(false);
        }
    }

    async function reEnableThirdPartyCamera(thirdPartyCameraId: number) {
        try {
            setIsRejectLoading(true);
            await thirdPartyApprovedCompaniesService.reEnableDonatedCamera(thirdPartyCameraId);
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });

            setRejectModalVisible(false);
            refetch();
        } catch (err) {
            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }

            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        } finally {
            setIsRejectLoading(false);
            setRejectModalVisible(false);
        }
    }

    return (
        <Container>
            <Header title={translate('donatedCameras')} />
            <Card>
                <DataTableHeader
                    title={translate('registeredDonatedCameras')}
                    zIndex={2}
                    filter={<Filter setTextFilter={setTextFilter} textFilter={textFilter} filters={filters} setFilters={setFilters} />}
                    filterInput={<FilterInput
                        value={textFilter}
                        onChange={setTextFilter}
                        placeholder={translate('typeToSearch')}
                        onKeyPress={handleKeyDown}
                    />}
                />
                <DataTableContainer
                    dataTable={<MyDataTable
                        columns={[
                            {
                                name: translate('externalId'),
                                selector: row => row.externalId,
                                wrap: true,
                            },
                            {
                                name: translate('title'),
                                selector: row => row.camera?.title || '',
                                grow: 3,
                                wrap: true,
                            },
                            {
                                name: translate('type'),
                                selector: row => translate(row.camera?.type || ''),
                                grow: 3,
                                wrap: true,
                            },
                            {
                                name: translate('deleteDate'),
                                omit: !filters.showRemovedByThirdParty,
                                selector: row => moment(row.deletedAt).format('DD/MM/YYYY HH:mm:ss'),
                            },
                            {
                                name: translate('integrationType'),
                                selector: row => translate(row.integrationType),
                            },
                            {
                                name: translate('integration'),
                                selector: row => row.thirdPartyCloudProvider && row.thirdPartyImageProvider ? `${row.thirdPartyCloudProvider.tradeName}-${row.thirdPartyImageProvider.tradeName}` : row.origin,
                                wrap: true
                            },
                            {
                                name: translate('registeredAt'),
                                selector: row => moment(row.createdAt).format('DD/MM/YYYY HH:mm'),
                                wrap: true
                            },
                            {
                                name: translate('action'),
                                button: true,
                                cell: row => {
                                    return (
                                        <DropdownButton
                                            actions={[{
                                                content: translate('watchCamera'),
                                                onPress: () => {
                                                    window.open(`/cameras/map/${row.camera?.id}`);
                                                },
                                                omit: !(
                                                    row.camera &&
                                                    row.deletedAt === null &&
                                                    !row.rejected
                                                )
                                            }, {
                                                content: translate('watchLprEvents'),
                                                onPress: () => window.open(`/LPR/live-events?cameras=${row.camera?.id}`, '_blank'),
                                                omit: !(
                                                    row.camera &&
                                                    row.camera.hasLpr &&
                                                    (
                                                        authenticatedUser?.isAdmin ||
                                                        authenticatedUser?.permissions.lpr_module_access
                                                    )
                                                )
                                            }, {
                                                content: translate('watchFacialEvents'),
                                                onPress: () => window.open(`/facial/live-events?cameras=${row.camera?.id}`, '_blank'),
                                                omit: !(
                                                    row.camera &&
                                                    row.camera.hasFacial &&
                                                    (
                                                        authenticatedUser?.isAdmin ||
                                                        authenticatedUser?.permissions.facial_module_access ||
                                                        authenticatedUser?.permissions.facial_events_missing_person ||
                                                        authenticatedUser?.permissions.facial_events_wanted_person
                                                    )
                                                )
                                            }, {
                                                content: row.rejected ? translate('enable') : translate('reject'),
                                                variant: row.rejected ? 'warning' : 'danger',
                                                onPress: () => {
                                                    setSelectedCamera(row);
                                                    setRejectModalVisible(true);
                                                },
                                                omit: !(row.deletedAt === null)
                                            }]}
                                        />
                                    );
                                }
                            }
                        ]}
                        data={cameras}
                        progressPending={isLoading}
                    />}
                    pagination={<Pagination
                        totalItems={totalItems}
                        currentPage={filters.page}
                        pageSize={filters.limit}
                        setPageSize={pageSize => {
                            setFilters(old => ({
                                ...old,
                                limit: pageSize,
                                page: 0,
                            }));

                            navigation.setParams({
                                ...route.params,
                                limit: pageSize,
                                page: 1,
                            });
                        }}
                        setPage={page => {
                            setFilters(old => ({
                                ...old,
                                page
                            }));

                            navigation.setParams({
                                ...route.params,
                                page: page + 1
                            });
                        }}
                    />}
                />
            </Card>
            {selectedCamera &&
                <DeleteModal
                    setModalVisible={() => { }}
                    onCancel={() => setRejectModalVisible(false)}
                    isModalVisible={rejectModalVisible}
                    customMessage={`${selectedCamera.rejected ? translate('confirm_enable') : translate('confirm_reject')}: ${selectedCamera?.camera?.title}?`}
                    customConfirm={selectedCamera.rejected ? translate('enable') : translate('reject')}
                    onSubmitType={selectedCamera.rejected ? 'primary' : 'destructive'}
                    itemName={selectedCamera.camera?.title}
                    onSubmit={async () => {
                        if (selectedCamera) {
                            if (selectedCamera.rejected) {
                                await reEnableThirdPartyCamera(selectedCamera.id);
                            } else {
                                await confirmDelete(selectedCamera);
                            }
                        }
                    }}
                    isLoading={isRejectLoading}
                />}

        </Container>
    );
}
